import React from "react";
import { Box, Typography } from "@mui/material";
import OrderActionButtons from "./OrderActionButtons";
import formatCartItemDetails from "../../../utils/formatCartItemDetails";
import formatCategoryLabel from "../../../utils/formatCategoryLabel";

const OrderItems = ({ items, receiptUrl }) => {
  return (
    <>
      {/* Order info header */}
      <Box className="print-section-header">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Items Ordered
          </Typography>
          <OrderActionButtons receiptUrl={receiptUrl} />
        </Box>
      </Box>

      {/* Items list */}
      <Box className="print-section">
        <Box className="screen-only">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}>
            {items.map((item, idx) => {
              const details = formatCartItemDetails(item);

              // Sum line-level discounts from the engine
              const totalLineDiscount = (item.discountsApplied || []).reduce(
                (sum, d) => sum + (d.amount || 0),
                0
              );
              const hasDiscount = totalLineDiscount > 0;

              // Optional: compact label for display ("15% OFF" / "$5.00 OFF" / "2 discounts")
              const discountLabel = (() => {
                const list = item.discountsApplied || [];
                if (list.length === 0) return null;
                if (list.length === 1) {
                  const d = list[0];
                  if (
                    d.valueType === "percentage" &&
                    typeof d.value === "number"
                  ) {
                    return `${Math.round(d.value)}% OFF`;
                  }
                  if (typeof d.value === "number") {
                    return `$${d.value.toFixed(2)} OFF`;
                  }
                  return d.name || "Discount";
                }
                return `${list.length} discounts`;
              })();

              // Prefer precomputed originals if present; otherwise derive
              const originalTotal =
                typeof item.originalTotalPrice === "number"
                  ? item.originalTotalPrice
                  : (item.originalUnitPrice ?? item.unitPrice ?? 0) *
                    (item.quantity || 1);

              const finalTotal =
                typeof item.totalPrice === "number"
                  ? item.totalPrice
                  : (item.unitPrice || 0) * (item.quantity || 1);

              return (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    p: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    minHeight: 120,
                    minWidth: "260px",
                    maxWidth: "100%",
                    breakInside: "avoid",
                  }}>
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.displayName || item.name}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Box flex="1">
                    <Typography variant="body1" fontWeight="bold">
                      {formatCategoryLabel(item)} × {item.quantity}
                    </Typography>

                    {details.map(({ label, value }, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        color="text.secondary">
                        {label}: {value}
                      </Typography>
                    ))}

                    {hasDiscount && (
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: "block", mt: 0.5 }}>
                        -${totalLineDiscount.toFixed(2)}
                        {discountLabel ? ` · ${discountLabel}` : ""}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mt: 0.5 }}>
                      ${finalTotal.toFixed(2)}
                      {hasDiscount && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: "line-through", ml: 1 }}>
                          ${originalTotal.toFixed(2)}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OrderItems;
